import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { api } from "../api";
import type {
  Review,
  ReviewFilters,
  ReviewsResponse,
  ReviewDashboardStats,
  ReviewThemes,
  ReviewPeriod,
  SuggestionRequest,
  SuggestionResponse,
  PublishResponse,
  MarkRespondedResponse,
  SyncResponse,
  AgiStatus,
  AgiConnectRequest,
  AgiConnectResponse,
  ReviewsChatRequest,
  ReviewsChatResponse,
  Channel,
} from "../../types/reviews";

function isNotFoundError(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 404;
}

const EMPTY_REVIEWS_RESPONSE: ReviewsResponse = {
  reviews: [],
  pagination: { page: 1, limit: 20, total: 0, totalPages: 0 },
  filters: {},
};

export function useReviews(filters?: ReviewFilters) {
  return useInfiniteQuery<ReviewsResponse>({
    queryKey: ["reviews", filters],
    queryFn: async ({ pageParam = 1 }) => {
      try {
        return await api.reputation.getReviews({ ...filters, page: pageParam as number });
      } catch (error) {
        if (isNotFoundError(error)) return EMPTY_REVIEWS_RESPONSE;
        throw error;
      }
    },
    getNextPageParam: (lastPage) => {
      const { page, totalPages } = lastPage.pagination;
      return page < totalPages ? page + 1 : undefined;
    },
    staleTime: 2 * 60 * 1000,
  });
}

export function useReview(id: string | null) {
  return useQuery<Review>({
    queryKey: ["reviews", id],
    queryFn: () => {
      if (!id) throw new Error("Review ID is required");
      return api.reputation.getReview(id);
    },
    enabled: !!id,
    staleTime: 2 * 60 * 1000,
  });
}

const EMPTY_STATS: ReviewDashboardStats = {
  total: 0,
  byChannel: { GOOGLE: 0, BOOKING: 0, EXPEDIA: 0 },
  bySentiment: { POSITIVE: 0, NEUTRAL: 0, NEGATIVE: 0 },
  responded: 0,
  pending: 0,
  averageRating: 0,
  averageResponseTimeHours: null,
};

export function useReviewStats(period?: ReviewPeriod) {
  return useQuery<ReviewDashboardStats>({
    queryKey: ["review-stats", period],
    queryFn: async () => {
      try {
        return await api.reputation.getStats(period);
      } catch (error) {
        if (isNotFoundError(error)) return EMPTY_STATS;
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000,
  });
}

const EMPTY_THEMES: ReviewThemes = {
  positiveThemes: [],
  negativeThemes: [],
  totalReviewsAnalyzed: 0,
};

export function useReviewThemes(period?: ReviewPeriod, channel?: Channel) {
  return useQuery<ReviewThemes>({
    queryKey: ["review-themes", period, channel ?? "all-channels"],
    queryFn: async () => {
      try {
        return await api.reputation.getThemes(period, channel);
      } catch (error) {
        if (isNotFoundError(error)) return EMPTY_THEMES;
        throw error;
      }
    },
    staleTime: 30 * 1000,
  });
}

export function useSuggestResponse() {
  return useMutation<
    SuggestionResponse,
    Error,
    { id: string; body?: SuggestionRequest }
  >({
    mutationFn: ({ id, body }) => api.reputation.suggest(id, body),
  });
}

export function usePublishResponse() {
  const queryClient = useQueryClient();

  return useMutation<PublishResponse, Error, { id: string; responseText: string }>({
    mutationFn: ({ id, responseText }) => api.reputation.publish(id, responseText),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["reviews"] });
      queryClient.invalidateQueries({ queryKey: ["reviews", id] });
      queryClient.invalidateQueries({ queryKey: ["review-stats"] });
    },
  });
}

export function useMarkResponded() {
  const queryClient = useQueryClient();

  return useMutation<MarkRespondedResponse, Error, { id: string; responseText: string }>({
    mutationFn: ({ id, responseText }) => api.reputation.markResponded(id, responseText),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["reviews"] });
      queryClient.invalidateQueries({ queryKey: ["reviews", id] });
      queryClient.invalidateQueries({ queryKey: ["review-stats"] });
    },
  });
}

export function useSyncReviews() {
  const queryClient = useQueryClient();

  return useMutation<SyncResponse, Error, void>({
    mutationFn: () => api.reputation.sync(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reviews"] });
      queryClient.invalidateQueries({ queryKey: ["review-stats"] });
    },
  });
}

const DISCONNECTED_STATUS: AgiStatus = { isConnected: false, gbpConnected: false };

export function useAgiStatus() {
  return useQuery<AgiStatus>({
    queryKey: ["agi-status"],
    queryFn: async () => {
      try {
        return await api.reputation.getStatus();
      } catch (error) {
        if (isNotFoundError(error)) return DISCONNECTED_STATUS;
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useConnectAgi() {
  const queryClient = useQueryClient();

  return useMutation<AgiConnectResponse, Error, AgiConnectRequest>({
    mutationFn: (data) => api.reputation.connect(data),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ["agi-status"] });
        queryClient.invalidateQueries({ queryKey: ["reviews"] });
        queryClient.invalidateQueries({ queryKey: ["review-stats"] });
        queryClient.invalidateQueries({ queryKey: ["review-themes"] });
      }
    },
  });
}

export function useReviewsChat() {
  return useMutation<ReviewsChatResponse, Error, ReviewsChatRequest>({
    mutationFn: (data) => api.reputation.chat(data),
  });
}
